"use client";

import {
	DISCOVERY_QUERY_TYPE_LABELS,
	type DiscoveryQueryType,
	PROMPT_GENERATOR_FRAMEWORK_LABELS,
	type PersonaDraft,
	type PromptGenerationResultItem,
	groupPromptGenerationResults,
	promptGeneratorFrameworkFor,
	promptGeneratorResultsUrl,
} from "@repo/core/client";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@ui/components/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/components/tabs";
import { CheckIcon, ClipboardIcon, DownloadIcon, LinkIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FRAMEWORK_FORM_DESCRIPTIONS } from "../lib/copy";
import {
	CSV_MORE_COLUMNS,
	CSV_PREVIEW_COLUMNS,
	buildResultsClipboardText,
	buildResultsCsv,
	personaCsvRows,
	promptCsvRows,
} from "../lib/export";
import { EmailPanelField } from "./EmailPanelField";
import {
	AddPersonasCard,
	PERSONA_PANEL_ANCHOR,
	PersonaPanel,
} from "./PersonaPanel";
import { TrackBuyersCta } from "./TrackBuyersCta";

/** Sub-type chip, shown only when it adds something beyond the tab name. */
function subTypeLabel(prompt: PromptGenerationResultItem): string | null {
	const framework = promptGeneratorFrameworkFor(prompt);
	const label =
		DISCOVERY_QUERY_TYPE_LABELS[prompt.queryType as DiscoveryQueryType];
	if (!label || label === PROMPT_GENERATOR_FRAMEWORK_LABELS[framework]) {
		return null;
	}
	if (label.startsWith("Buyer's Journey (")) {
		return label.slice("Buyer's Journey (".length, -1);
	}
	if (
		framework === "category_entry_points" ||
		framework === "jobs_to_be_done" ||
		framework === "stakeholder_perspectives"
	) {
		return null;
	}
	return label;
}

function useFlash(ms = 2000) {
	const [on, setOn] = useState(false);
	useEffect(() => {
		if (!on) {
			return;
		}
		const t = setTimeout(() => setOn(false), ms);
		return () => clearTimeout(t);
	}, [on, ms]);
	return [on, () => setOn(true)] as const;
}

function downloadCsv(csv: string, filename: string) {
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

export interface PromptResultsPanelProps {
	generationId: string;
	domain: string;
	results: readonly PromptGenerationResultItem[];
	/** Empty when the result has no personas (off, or drafting failed). */
	personas: readonly PersonaDraft[];
	segments: readonly string[];
	emailSent: boolean;
	/** Back to the form with Personas on (a no-personas result). */
	onAddPersonas: () => void;
	onPersonasViewed?: () => void;
	onExport?: (format: "csv" | "clipboard") => void;
	onEmailSent?: () => void;
	onTrackClick?: () => void;
}

/**
 * Results for the free tool. The results card grows to the full content
 * width: prompts (tabbed by framework) on the left, the persona panel on the
 * right, and the next steps (CTA, then the optional email) under the prompts,
 * sticky on desktop so they stay beside the persona cards.
 *
 * DOM order is prompts, personas, next steps, so a phone reads prompts →
 * personas → CTA → email.
 */
export function PromptResultsPanel({
	generationId,
	domain,
	results,
	personas,
	segments,
	emailSent,
	onAddPersonas,
	onPersonasViewed,
	onExport,
	onEmailSent,
	onTrackClick,
}: PromptResultsPanelProps) {
	const groups = useMemo(
		() => groupPromptGenerationResults([...results]),
		[results],
	);
	const withPersonas = personas.length > 0;
	const [copied, flashCopied] = useFlash();
	const [linkCopied, flashLinkCopied] = useFlash();
	const [origin, setOrigin] = useState<string | null>(null);

	useEffect(() => {
		setOrigin(window.location.origin);
	}, []);

	const shareUrl = promptGeneratorResultsUrl(generationId, {
		absoluteBase: origin,
	});
	const shareLabel = shareUrl.replace(/^https?:\/\//, "");

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl);
			flashLinkCopied();
		} catch {
			toast.error("Couldn't copy the link");
		}
	};

	const handleCopyAll = async () => {
		try {
			await navigator.clipboard.writeText(
				buildResultsClipboardText(results, personas),
			);
			flashCopied();
			toast.success("Copied to clipboard");
			onExport?.("clipboard");
		} catch {
			toast.error("Couldn't copy to the clipboard");
		}
	};

	const handleExportCsv = () => {
		downloadCsv(
			buildResultsCsv(results, personas),
			`ai-visibility-prompts-${domain.replace(/[^a-z0-9.-]/gi, "-")}.csv`,
		);
		toast.success("CSV downloaded");
		onExport?.("csv");
	};

	return (
		<div className="w-full space-y-6">
			<div className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
				<p className="text-sm text-muted-foreground">
					Results for{" "}
					<span className="font-semibold text-foreground">
						{domain}
					</span>
				</p>
				<div className="flex min-w-0 items-center gap-2">
					<p className="hidden min-w-0 truncate font-mono text-xs text-muted-foreground sm:block">
						{shareLabel}
					</p>
					<Button
						variant="ghost"
						size="sm"
						className="shrink-0"
						onClick={handleCopyLink}
						aria-label={`Copy link: ${shareLabel}`}
					>
						{linkCopied ? (
							<CheckIcon className="size-3.5" />
						) : (
							<LinkIcon className="size-3.5" />
						)}
						{linkCopied ? "Copied" : "Copy link"}
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-5 lg:grid-rows-[auto_1fr] lg:gap-8">
				{/* Prompts */}
				<section className="min-w-0 space-y-4 lg:col-span-3">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
						<div>
							<p className="text-sm text-muted-foreground">
								<span className="font-semibold text-foreground">
									{results.length}
								</span>{" "}
								prompts across {groups.length} framework
								{groups.length === 1 ? "" : "s"}
								{withPersonas ? (
									<>
										{" "}
										and{" "}
										<span className="font-semibold text-foreground">
											{personas.length}
										</span>{" "}
										personas
									</>
								) : null}
							</p>
							{withPersonas ? (
								<p className="mt-0.5 text-xs text-muted-foreground">
									Copy All and Export CSV include your
									personas.{" "}
									<CsvLayoutPopover
										results={results}
										personas={personas}
									/>
								</p>
							) : null}
						</div>
						<div className="flex shrink-0 items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={handleCopyAll}
							>
								{copied ? (
									<CheckIcon className="size-3.5" />
								) : (
									<ClipboardIcon className="size-3.5" />
								)}
								{copied ? "Copied!" : "Copy All"}
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleExportCsv}
							>
								<DownloadIcon className="size-3.5" />
								Export CSV
							</Button>
						</div>
					</div>

					{groups.length > 0 ? (
						<Tabs
							defaultValue={groups[0]?.framework}
							className="w-full"
						>
							<TabsList className="flex h-auto w-full justify-start gap-x-1 gap-y-1 overflow-x-auto border-b-0 pb-0.5 [mask-image:linear-gradient(to_right,black_85%,transparent)] sm:flex-wrap sm:overflow-visible sm:[mask-image:none]">
								{groups.map((g) => (
									<TabsTrigger
										key={g.framework}
										value={g.framework}
										className="gap-1.5 text-xs"
									>
										{g.label}
										<span className="tabular-nums text-muted-foreground">
											{g.prompts.length}
										</span>
									</TabsTrigger>
								))}
							</TabsList>
							{groups.map((g) => (
								<TabsContent
									key={g.framework}
									value={g.framework}
									className="mt-4 space-y-3"
								>
									<p className="text-xs text-muted-foreground">
										{
											FRAMEWORK_FORM_DESCRIPTIONS[
												g.framework
											]
										}
									</p>
									<div className="space-y-2">
										{g.prompts.map((prompt, i) => {
											const sub = subTypeLabel(prompt);
											return (
												<div
													key={
														prompt.id ||
														`${g.framework}-${i}`
													}
													className="rounded-lg border bg-card p-4 text-sm"
												>
													<p className="text-foreground">
														{prompt.query}
													</p>
													{sub ||
													prompt.segment ||
													prompt.location ? (
														<div className="mt-2 flex flex-wrap gap-1.5">
															{sub ? (
																<span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
																	{sub}
																</span>
															) : null}
															{prompt.segment ? (
																<Badge
																	status="info"
																	className="text-[10px]"
																>
																	{
																		prompt.segment
																	}
																</Badge>
															) : null}
															{prompt.location ? (
																<Badge
																	status="info"
																	className="text-[10px]"
																>
																	{
																		prompt.location
																	}
																</Badge>
															) : null}
														</div>
													) : null}
												</div>
											);
										})}
									</div>
									{g.framework ===
										"stakeholder_perspectives" &&
									withPersonas ? (
										<p className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
											These prompts are written from each
											role's point of view. Your personas
											go one step further: they keep any
											prompt word for word and ask it as
											that person.{" "}
											<a
												href={`#${PERSONA_PANEL_ANCHOR}`}
												className="font-semibold text-primary hover:underline"
											>
												See your personas
											</a>
										</p>
									) : null}
								</TabsContent>
							))}
						</Tabs>
					) : null}
				</section>

				{/* Personas (or the gentle prompt to add them) */}
				<div className="min-w-0 space-y-4 lg:col-span-2 lg:col-start-4 lg:row-span-2 lg:row-start-1">
					{withPersonas ? (
						<PersonaPanel
							personas={personas}
							segments={segments}
							onViewed={onPersonasViewed}
						/>
					) : (
						<AddPersonasCard
							segments={segments}
							onAdd={onAddPersonas}
						/>
					)}
				</div>

				{/* Next steps: CTA, then the optional email. Sticky on desktop
				    so both stay in view while the persona column scrolls. */}
				<div className="min-w-0 space-y-4 lg:sticky lg:top-36 lg:col-span-3 lg:col-start-1 lg:row-start-2 lg:self-start">
					<TrackBuyersCta
						generationId={generationId}
						promptCount={results.length}
						personaCount={personas.length}
						onClick={onTrackClick}
					/>
					{/* The email carries the persona panel, so it is offered
					    only when there is one. */}
					{withPersonas ? (
						<EmailPanelField
							generationId={generationId}
							alreadySent={emailSent}
							onSent={onEmailSent}
						/>
					) : null}
				</div>
			</div>
		</div>
	);
}

/**
 * "See the file layout": one CSV, prompts then personas, previewed with the
 * first few rows of each.
 */
function CsvLayoutPopover({
	results,
	personas,
}: {
	results: readonly PromptGenerationResultItem[];
	personas: readonly PersonaDraft[];
}) {
	const promptRows = promptCsvRows(results.slice(0, 3));
	const personaRows = personaCsvRows(personas).slice(0, 2);
	const morePrompts = results.length - promptRows.length;
	const morePersonas = personas.length - personaRows.length;

	const renderRow = (row: Record<string, string>, key: string) => (
		<tr key={key}>
			{CSV_PREVIEW_COLUMNS.map((column) => (
				<td
					key={column}
					className="max-w-28 truncate px-2 py-1.5 align-top"
				>
					{row[column] || (
						<span className="text-muted-foreground">·</span>
					)}
				</td>
			))}
		</tr>
	);
	const moreRow = (key: string, text: string) => (
		<tr key={key}>
			<td
				colSpan={CSV_PREVIEW_COLUMNS.length}
				className="px-2 py-1 text-muted-foreground italic"
			>
				{text}
			</td>
		</tr>
	);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="font-semibold text-primary hover:underline"
				>
					See the file layout
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				className="w-[min(48rem,calc(100vw-2rem))] space-y-2 p-3"
			>
				<p className="text-sm font-semibold">
					One CSV, prompts then personas
				</p>
				<p className="text-xs text-muted-foreground">
					{results.length} prompt rows, then {personas.length} persona
					rows. Filter the Type column to split them.
				</p>
				<div className="overflow-x-auto rounded-md border">
					<table className="w-max min-w-full text-left text-[11px]">
						<thead className="bg-muted/60 text-muted-foreground">
							<tr>
								{CSV_PREVIEW_COLUMNS.map((c) => (
									<th
										key={c}
										scope="col"
										className="whitespace-nowrap px-2 py-1.5 font-semibold"
									>
										{c}
									</th>
								))}
							</tr>
						</thead>
						<tbody className="divide-y">
							{promptRows.map((row, i) =>
								renderRow(row, `p-${i}`),
							)}
							{morePrompts > 0
								? moreRow(
										"more-prompts",
										`${morePrompts} more prompt rows`,
									)
								: null}
							{personaRows.map((row, i) =>
								renderRow(row, `q-${i}`),
							)}
							{morePersonas > 0
								? moreRow(
										"more-personas",
										`${morePersonas} more persona rows`,
									)
								: null}
						</tbody>
					</table>
				</div>
				<p className="text-xs text-muted-foreground">
					Also in the file: {CSV_MORE_COLUMNS}.
				</p>
			</PopoverContent>
		</Popover>
	);
}
